import { memo, type ReactNode, useCallback } from 'react';

import { useTrackActivatedIntercom, useTrackOpenInMobileAppClicked, useTrackOpenIntercomChat } from '@air/analytics';
import { InfoIcon, LightbulbIcon, MegaphoneIcon, MessagesSquareIcon, PhoneIcon, QrCodeIcon } from '@air/design-icons';
import { DropdownMenuItem, DropdownMenuSeparator } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';

import { DemoFormModal } from '~/components/Modals/DemoFormModal';
import { OpenOnPhoneModal } from '~/components/Modals/OpenOnPhoneModal';
import { useCannyUrl } from '~/components/v2/hooks/useCannyUrl';
import { Routes } from '~/constants/routes';
import { NEW_TAB } from '~/constants/urls';

export type HelpDropdownMenuItemsProps = {
  additionalItems?: ReactNode;
};

export const HelpDropdownMenuItems = memo(({ additionalItems }: HelpDropdownMenuItemsProps) => {
  const [showDemoFormModal] = useAirModal(DemoFormModal);
  const [showOpenOnPhoneModal] = useAirModal(OpenOnPhoneModal);
  const { cannyUrl } = useCannyUrl();
  const { trackActivatedIntercom } = useTrackActivatedIntercom();
  const { trackOpenInMobileAppClicked } = useTrackOpenInMobileAppClicked();
  const { trackOpenIntercomChat } = useTrackOpenIntercomChat();

  const handleCannySelect = useCallback(() => {
    if (cannyUrl) {
      window.open(cannyUrl, NEW_TAB);
    }
  }, [cannyUrl]);

  const handleChatWithSupportSelect = useCallback(() => {
    trackActivatedIntercom({ location: 'workspace-sidebar' });
    trackOpenIntercomChat();
  }, [trackActivatedIntercom, trackOpenIntercomChat]);

  const handleHelpCenterSelect = useCallback(() => {
    window.open(Routes.helpCenter, NEW_TAB);
  }, []);

  const handleMeetWithSalesSelect = useCallback(() => {
    showDemoFormModal();
  }, [showDemoFormModal]);

  const handleNewAtAirSelect = useCallback(() => {
    window.open(Routes.changelog, NEW_TAB);
  }, []);

  const handleOpenInMobileAppSelect = useCallback(() => {
    trackOpenInMobileAppClicked({ location: 'help_menu' });
    showOpenOnPhoneModal();
  }, [showOpenOnPhoneModal, trackOpenInMobileAppClicked]);

  return (
    <>
      <DropdownMenuItem label="Help Center" onSelect={handleHelpCenterSelect} prefix={<InfoIcon size="sm" />} />
      <DropdownMenuItem
        label="Chat with Support"
        onSelect={handleChatWithSupportSelect}
        prefix={<MessagesSquareIcon size="sm" />}
      />
      <DropdownMenuItem label="Meet with Sales" onSelect={handleMeetWithSalesSelect} prefix={<PhoneIcon size="sm" />} />
      <DropdownMenuItem label="New at Air" onSelect={handleNewAtAirSelect} prefix={<MegaphoneIcon size="sm" />} />
      <DropdownMenuItem
        label="Open in mobile app"
        onSelect={handleOpenInMobileAppSelect}
        prefix={<QrCodeIcon size="sm" />}
      />
      <DropdownMenuItem
        disabled={!cannyUrl}
        label="Leave feedback"
        onSelect={handleCannySelect}
        prefix={<LightbulbIcon size="sm" />}
      />
      {additionalItems ? (
        <>
          <DropdownMenuSeparator />
          {additionalItems}
        </>
      ) : null}
    </>
  );
});

HelpDropdownMenuItems.displayName = 'HelpDropdownMenuItems';
